import { VisitTypeIds } from '@router/loaders/utils';
import {
  SCHEDULE_ROUTE_PATHNAME,
  SERVICES_ROUTE_PATHNAME,
} from '@router/routes/routePathnames';
import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/**
 * Centralized hook to set the document title.
 * - If a `title` is provided, it will be used directly.
 * - Otherwise, it derives the title from the current pathname and search params.
 * Optionally restores the previous title on unmount.
 */
export function useDocumentTitle(
  title?: string,
  options?: { restoreOnUnmount?: boolean; prevTitle?: string },
) {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const previous = document.title;
    const pathname = location.pathname;

    let computedTitle = title;
    let computedDescription = '';

    if (!computedTitle) {
      const utmService = searchParams.get('utm_service');
      const stateParam = searchParams.get('state');

      if (pathname === '/') {
        computedTitle =
          utmService === VisitTypeIds.AddictionTreatment
            ? 'Booking | QuickMD'
            : 'QuickMD Booking';
        computedDescription =
          utmService === VisitTypeIds.AddictionTreatment
            ? 'Schedule online care with QuickMD. Get fast access to licensed providers for addiction, hormone therapy, weight loss, urgent health needs, and more.'
            : 'Book virtual care with QuickMD for addiction treatment, hormone therapy, weight loss, and urgent health needs.';
      } else if (pathname.startsWith(SERVICES_ROUTE_PATHNAME)) {
        if (stateParam) {
          computedTitle = `QuickMD Booking ${stateParam}`;
          computedDescription = `See available QuickMD telehealth services in ${stateParam}. Book online care with licensed providers from home.`;
        } else if (pathname === SERVICES_ROUTE_PATHNAME) {
          computedTitle = 'Booking Services | QuickMD';
          computedDescription =
            'Explore QuickMD services, including addiction treatment, hormone therapy, weight loss, and urgent health needs—all online.';
        } else {
          computedTitle = 'QuickMD Booking Services';
          computedDescription =
            'View all QuickMD telemedicine services. Get convenient, affordable virtual care from licensed providers nationwide.';
        }
      } else if (pathname.startsWith(SCHEDULE_ROUTE_PATHNAME)) {
        computedTitle = 'Scheduling | QuickMD';
        computedDescription =
          'Schedule a QuickMD appointment in minutes. Get virtual care from licensed providers—no waiting rooms, no hassle.';
      } else {
        computedTitle = 'QuickMD Booking';
      }
    }

    document.title = computedTitle;

    const meta = document.querySelector(
      'meta[name="description"]',
    ) as HTMLMetaElement | null;
    if (computedDescription) {
      const ensuredMeta = meta ?? document.createElement('meta');
      if (!meta) {
        ensuredMeta.name = 'description';
        document.head.appendChild(ensuredMeta);
      }
      ensuredMeta.content = computedDescription;
    } else if (meta) {
      document.head.removeChild(meta);
    }
    console.log(
      'Description meta content:',
      document
        .querySelector('meta[name="description"]')
        ?.getAttribute('content'),
    );

    return () => {
      if (options?.restoreOnUnmount) {
        document.title = options.prevTitle ?? previous;
      }
    };
  }, [
    title,
    location.pathname,
    options?.restoreOnUnmount,
    options?.prevTitle,
    searchParams,
  ]);
}
